import { Description } from "@mui/icons-material";
import { ApiSimpleTenant } from "@incendium/api";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import {
  buildSamlMicrosoftReplyUrl,
  buildSamlClientSetupInstructionsText,
} from "features/sso/samlClientSetupInstructions";
import { useState } from "react";

export interface SamlClientSetupDialogProps {
  tenant: ApiSimpleTenant;
}

function SamlClientSetupDialog({ tenant }: SamlClientSetupDialogProps) {
  const [open, setOpen] = useState(false);
  const tenantId = tenant.id;
  const canOpen = Boolean(tenantId);
  const replyUrl = canOpen ? buildSamlMicrosoftReplyUrl(tenantId!) : "";
  const instructionsText = canOpen
    ? buildSamlClientSetupInstructionsText({
        tenantId: tenantId!,
        entityId: tenant.entityId || undefined,
      })
    : "";

  return (
    <>
      <IconButton
        size="small"
        aria-label="Client SSO setup instructions"
        onClick={() => setOpen(true)}
        disabled={!canOpen}
      >
        <Description fontSize="small" />
      </IconButton>

      <CustomDialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Client SSO setup (Microsoft Entra)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Share these steps with your client&apos;s IT team. Incendium will
            complete SSO configuration once the client sends back the
            Federation Metadata URL.
          </Typography>

          <Typography variant="body2" component="div" mb={1.5}>
            Use your client&apos;s Microsoft Entra ID tenant. Any Entra ID
            tenant is supported; Entra ID is required.
          </Typography>

          <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
            <Typography component="li" variant="body2" gutterBottom>
              In Microsoft Entra, create or configure an Enterprise application
              with SAML single sign-on.
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              Ensure "Require verification certificates" is disabled
              (unchecked).
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              Set the <strong>Reply URL</strong> (Assertion Consumer Service
              URL) to:
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              mb: 2,
              pl: 2.5,
              py: 1,
              px: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="body2"
              component="code"
              sx={{ flex: 1, wordBreak: "break-all" }}
            >
              {replyUrl}
            </Typography>
            <CopyClipboardBtn
              text={replyUrl}
              isIconButton
              size="small"
              aria-label="Copy Reply URL"
            />
          </Stack>

          <Box component="ol" start={4} sx={{ pl: 2.5, m: 0 }}>
            <Typography component="li" variant="body2" gutterBottom>
              Set the <strong>enterprise application name</strong> (Entity
              ID) to: {tenant.entityId}
            </Typography>
            <Typography component="li" variant="body2" gutterBottom>
              From the app&apos;s SAML certificate, copy the{" "}
              <strong>App Federation Metadata URL</strong> and send it to
              Incendium.
            </Typography>
            <Typography component="li" variant="body2">
              After Incendium receives the Federation Metadata URL, we will
              complete SSO configuration on our side.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <CopyClipboardBtn
            text={instructionsText}
            variant="contained"
            color="primary"
          >
            Copy instructions
          </CopyClipboardBtn>
        </DialogActions>
      </CustomDialog>
    </>
  );
}

export default SamlClientSetupDialog;
